import { logger } from '../../../utils/logger.js'
import maplibregl from 'maplibre-gl'
import { isGeoJSONSource } from '../map-engine.js'
import {
  bboxPolygon,
  point,
  lineString,
  polygon,
  circle,
  booleanPointInPolygon,
  booleanDisjoint,
} from '@turf/turf'
import {
  tripFeatureManager,
  type LatLng as FeatureLatLng,
} from '../../../services/trip-feature-manager.js'
import { selectionService } from '../../../services/selection/selection-service.js'
import { featureRefKey } from '../../../services/selection/selectable-feature-ref.js'
import {
  getSelectableOverlayLayerIds,
  mapGeoJSONFeatureToRef,
} from '../../../services/selection/query-helpers.js'
import { backcountryOverlayService } from '../../../services/backcountry/backcountry-overlay-service.js'
const log = logger.create('map')

import {
  haversineDistance,
  circleToGeoJSONCoords,
  screenToLngLat,
  touchToLngLat as touchToLngLatUtil,
} from '../geo-utils.js'
import type { Feature } from '../../../schemas/feature.js'
import { BaseShape } from '../shapes/base-shape.js'

export interface DragDrawingCallbacks {
  onFeatureCreated: (feature: Feature) => void
  onFeatureSelected: (featureId: string) => void
  updateContextMode: () => void
  getShapeComponent: (featureId: string) => BaseShape | undefined
  getShapeComponents: () => Map<string, BaseShape>
  getSelectedFeatures: () => Feature[]
  disableMapPanning: () => void
  enableMapPanning: () => void
  disableMapInteraction: () => void
  exitAllEditModes: () => void
  hideAllEditHandles: () => void
  updateSelectedFeatures: () => Promise<void>
  cleanupTempDrawing: () => void
  onVertexSelectionRect?:
    | ((bounds: { west: number; east: number; south: number; north: number }) => void)
    | undefined
}

interface MapLayerRefs {
  sourceId: string
  fillLayerId: string
  strokeLayerId: string
}

export class DragDrawingManager {
  private map: maplibregl.Map | undefined

  // Circle drawing state
  private circleStartPoint: maplibregl.LngLat | undefined

  // Rectangle drawing state
  private rectangleStartPoint: maplibregl.LngLat | undefined

  // Selection rectangle state
  private selectionRectStartPoint: maplibregl.LngLat | undefined
  private selectionRectSourceId = 'selection-rect'
  private selectionRectFillLayerId = 'selection-rect-fill'
  private selectionRectStrokeLayerId = 'selection-rect-stroke'
  private selectionRect: MapLayerRefs | undefined

  // Shared drag state. Box-select is now driven by GestureController via the
  // public begin/update/endBoxSelect API below; this manager only owns the
  // circle/rectangle drawing drags plus the selection-rect rendering helpers.
  private activeDragType: 'circle' | 'rectangle' | 'selection' | undefined
  private multiTouchActive = false

  // Temp drawing layers (shared with line/polygon for circle/rect preview)
  private tempDrawingSourceId = 'temp-drawing'
  private tempDrawingFillLayerId = 'temp-drawing-fill'
  private tempDrawingStrokeLayerId = 'temp-drawing-stroke'
  private tempDrawingLayer: MapLayerRefs | undefined

  // Native event handlers
  private boundNativeMouseDown: ((e: MouseEvent) => void) | undefined
  private boundNativeDragMove: ((e: MouseEvent) => void) | undefined
  private boundNativeDragEnd: ((e: MouseEvent) => void) | undefined

  constructor(private callbacks: DragDrawingCallbacks) {}

  setMap(map: maplibregl.Map): void {
    this.map = map
  }

  get isActiveDrag(): boolean {
    return !!this.activeDragType
  }

  get isMultiTouch(): boolean {
    return this.multiTouchActive
  }

  // === Box-select public API (driven by GestureController) ===

  /** Begin a box-select at the given corner. */
  beginBoxSelect(latlng: maplibregl.LngLat): void {
    this.activeDragType = 'selection'
    this.callbacks.disableMapPanning()
    this.startSelectionRect(latlng)
  }

  /** Update the in-progress box-select rectangle. */
  updateBoxSelect(latlng: maplibregl.LngLat): void {
    this.updateSelectionRect(latlng)
  }

  /** Finish the box-select; `additive` keeps the existing selection. */
  async endBoxSelect(latlng: maplibregl.LngLat, additive = false): Promise<void> {
    this.activeDragType = undefined
    await this.finishSelectionRect(latlng, additive)
  }

  setupEventListeners(): void {
    if (!this.map) {
      return
    }

    this.boundNativeMouseDown = this.handleNativeMouseDown.bind(this)
    this.map.getContainer().addEventListener('mousedown', this.boundNativeMouseDown, true)

    const mapContainer = this.map.getContainer()
    mapContainer.addEventListener('touchstart', this.handleDrawTouchStart.bind(this), {
      passive: false,
    })
    mapContainer.addEventListener('touchmove', this.handleDrawTouchMove.bind(this), {
      passive: false,
    })
    mapContainer.addEventListener('touchend', this.handleDrawTouchEnd.bind(this), {
      passive: false,
    })
  }

  private nativeEventToLatLng(e: MouseEvent): maplibregl.LngLat {
    return screenToLngLat(this.map!, e.clientX, e.clientY)
  }

  private touchToLatLng(touch: Touch): maplibregl.LngLat | null {
    if (!this.map) {
      return null
    }
    return touchToLngLatUtil(this.map, touch)
  }

  private handleNativeMouseDown(e: MouseEvent): void {
    if (!this.map) {
      return
    }
    if (this.callbacks.getSelectedFeatures().length > 0) {
      return
    }

    // Only circle/rectangle drawing drags are owned here. Box-select (incl.
    // shift+drag) is recognised by GestureController and routed through the
    // begin/update/endBoxSelect API.
    let dragType: 'circle' | 'rectangle' | null = null
    if (
      tripFeatureManager.getMapMode() === 'draw' &&
      tripFeatureManager.getDrawSubContext() === 'tools'
    ) {
      const tool = tripFeatureManager.getSelectedTool()
      if (tool === 'circle') {
        dragType = 'circle'
      } else if (tool === 'rectangle') {
        dragType = 'rectangle'
      }
    }

    if (!dragType) {
      return
    }

    const latlng = this.nativeEventToLatLng(e)
    e.stopPropagation()
    e.preventDefault()
    this.activeDragType = dragType
    this.callbacks.disableMapPanning()

    if (dragType === 'circle') {
      this.startCircleDrag(latlng)
    } else {
      this.startRectangleDrag(latlng)
    }

    this.setupMouseDragListeners()
  }

  private setupMouseDragListeners(): void {
    this.boundNativeDragMove = (me: MouseEvent) => {
      me.preventDefault()
      const pos = this.nativeEventToLatLng(me)
      if (this.activeDragType === 'circle') {
        this.updateCircleDrag(pos)
      } else if (this.activeDragType === 'rectangle') {
        this.updateRectangleDrag(pos)
      }
    }

    this.boundNativeDragEnd = (me: MouseEvent) => {
      document.removeEventListener('mousemove', this.boundNativeDragMove!)
      document.removeEventListener('mouseup', this.boundNativeDragEnd!)
      this.boundNativeDragMove = undefined
      this.boundNativeDragEnd = undefined

      me.preventDefault()
      const pos = this.nativeEventToLatLng(me)
      const type = this.activeDragType
      this.activeDragType = undefined

      if (type === 'circle') {
        void this.finishCircleDrag(pos)
      } else if (type === 'rectangle') {
        void this.finishRectangleDrag(pos)
      }
    }

    document.addEventListener('mousemove', this.boundNativeDragMove)
    document.addEventListener('mouseup', this.boundNativeDragEnd)
  }

  handleDrawTouchStart(e: TouchEvent): void {
    if (this.callbacks.getSelectedFeatures().length > 0) {
      return
    }
    if (e.touches.length > 1) {
      this.multiTouchActive = true
      if (this.activeDragType) {
        if (this.activeDragType === 'circle') {
          this.cleanupCircleDrag()
        } else if (this.activeDragType === 'rectangle') {
          this.cleanupRectangleDrag()
        } else if (this.selectionRect) {
          this.map!.removeLayer(this.selectionRect.fillLayerId)
          this.map!.removeLayer(this.selectionRect.strokeLayerId)
          this.map!.removeSource(this.selectionRect.sourceId)
          this.selectionRect = undefined
          this.selectionRectStartPoint = undefined
        }
        this.activeDragType = undefined
        this.callbacks.enableMapPanning()
      }
      return
    }
    if (e.touches.length !== 1) {
      return
    }

    // Only circle/rectangle drawing drags are owned here. Box-select is driven
    // by GestureController.
    let dragType: 'circle' | 'rectangle' | null = null
    if (
      tripFeatureManager.getMapMode() === 'draw' &&
      tripFeatureManager.getDrawSubContext() === 'tools'
    ) {
      const tool = tripFeatureManager.getSelectedTool()
      if (tool === 'circle') {
        dragType = 'circle'
      } else if (tool === 'rectangle') {
        dragType = 'rectangle'
      }
    }

    if (!dragType) {
      return
    }

    const latlng = this.touchToLatLng(e.touches[0])
    if (!latlng) {
      return
    }

    e.preventDefault()
    e.stopPropagation()
    this.activeDragType = dragType
    this.callbacks.disableMapPanning()

    if (dragType === 'circle') {
      this.startCircleDrag(latlng)
    } else {
      this.startRectangleDrag(latlng)
    }
  }

  handleDrawTouchMove(e: TouchEvent): void {
    if (e.touches.length !== 1) {
      this.multiTouchActive = true
      if (this.activeDragType) {
        if (this.activeDragType === 'circle') {
          this.cleanupCircleDrag()
        } else if (this.activeDragType === 'rectangle') {
          this.cleanupRectangleDrag()
        } else if (this.selectionRect) {
          this.map!.removeLayer(this.selectionRect.fillLayerId)
          this.map!.removeLayer(this.selectionRect.strokeLayerId)
          this.map!.removeSource(this.selectionRect.sourceId)
          this.selectionRect = undefined
          this.selectionRectStartPoint = undefined
        }
        this.activeDragType = undefined
        this.callbacks.enableMapPanning()
      }
      return
    }

    // Box-select ('selection') is driven by GestureController; this manager only
    // advances circle/rectangle drawing drags.
    if (this.activeDragType !== 'circle' && this.activeDragType !== 'rectangle') {
      return
    }

    const latlng = this.touchToLatLng(e.touches[0])
    if (!latlng) {
      return
    }
    e.preventDefault()
    e.stopPropagation()

    if (this.activeDragType === 'circle') {
      this.updateCircleDrag(latlng)
    } else {
      this.updateRectangleDrag(latlng)
    }
  }

  handleDrawTouchEnd(e: TouchEvent): void {
    if (this.multiTouchActive) {
      if (e.touches.length === 0) {
        this.multiTouchActive = false
      }
      return
    }
    // Box-select ('selection') is finished by GestureController; this manager
    // only finishes circle/rectangle drawing drags.
    if (this.activeDragType !== 'circle' && this.activeDragType !== 'rectangle') {
      return
    }

    const latlng = e.changedTouches.length > 0 ? this.touchToLatLng(e.changedTouches[0]) : null
    if (!latlng) {
      return
    }
    e.preventDefault()
    e.stopPropagation()

    const type = this.activeDragType
    this.activeDragType = undefined

    if (type === 'circle') {
      void this.finishCircleDrag(latlng)
    } else {
      void this.finishRectangleDrag(latlng)
    }
  }

  // === Circle Drag ===

  private startCircleDrag(center: maplibregl.LngLat): void {
    log.debug('🔵 Starting circle drag at:', center)
    this.callbacks.disableMapPanning()
    this.circleStartPoint = center

    const circleCoords = circleToGeoJSONCoords([center.lng, center.lat], 10)

    if (this.map) {
      this.map.addSource(this.tempDrawingSourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [circleCoords] },
          properties: {},
        },
      })

      this.map.addLayer({
        id: this.tempDrawingFillLayerId,
        type: 'fill',
        source: this.tempDrawingSourceId,
        paint: { 'fill-color': '#3f5a7a', 'fill-opacity': 0.3 },
      })

      this.map.addLayer({
        id: this.tempDrawingStrokeLayerId,
        type: 'line',
        source: this.tempDrawingSourceId,
        paint: {
          'line-color': '#3f5a7a',
          'line-width': 2,
          'line-opacity': 0.8,
          'line-dasharray': [5, 5],
        },
      })

      this.tempDrawingLayer = {
        sourceId: this.tempDrawingSourceId,
        fillLayerId: this.tempDrawingFillLayerId,
        strokeLayerId: this.tempDrawingStrokeLayerId,
      }
      log.debug('🔵 ✅ Initial circle preview added to map')
    }
  }

  private updateCircleDrag(currentPoint: maplibregl.LngLat): void {
    if (!this.circleStartPoint || !this.tempDrawingLayer) {
      return
    }

    const radius = haversineDistance(
      this.circleStartPoint.lat,
      this.circleStartPoint.lng,
      currentPoint.lat,
      currentPoint.lng
    )

    try {
      const circleCoords = circleToGeoJSONCoords(
        [this.circleStartPoint.lng, this.circleStartPoint.lat],
        radius
      )

      if (this.map) {
        const source = this.map.getSource(this.tempDrawingSourceId)
        if (isGeoJSONSource(source)) {
          source.setData({
            type: 'Feature',
            geometry: { type: 'Polygon', coordinates: [circleCoords] },
            properties: {},
          })
        }
      }
    } catch (error) {
      log.error('🔵 ❌ Error updating circle radius:', error)
    }
  }

  private async finishCircleDrag(endPoint: maplibregl.LngLat): Promise<void> {
    if (!this.circleStartPoint) {
      return
    }

    const radius = haversineDistance(
      this.circleStartPoint.lat,
      this.circleStartPoint.lng,
      endPoint.lat,
      endPoint.lng
    )

    if (radius >= 5) {
      await this.createCircleFeature(this.circleStartPoint, radius)
    } else {
      log.debug('🚫 Circle too small, canceling')
    }

    this.cleanupCircleDrag()
    this.callbacks.enableMapPanning()
  }

  cleanupCircleDrag(): void {
    this.circleStartPoint = undefined
    this.removeTempDrawing()
  }

  // === Rectangle Drag ===

  private startRectangleDrag(startPoint: maplibregl.LngLat): void {
    log.debug('🟪 Starting rectangle drag at:', startPoint)
    this.callbacks.disableMapPanning()
    this.rectangleStartPoint = startPoint

    const minRectCoords = [
      [startPoint.lng, startPoint.lat],
      [startPoint.lng + 0.0001, startPoint.lat],
      [startPoint.lng + 0.0001, startPoint.lat + 0.0001],
      [startPoint.lng, startPoint.lat + 0.0001],
      [startPoint.lng, startPoint.lat],
    ]

    if (this.map) {
      this.map.addSource(this.tempDrawingSourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [minRectCoords] },
          properties: {},
        },
      })

      this.map.addLayer({
        id: this.tempDrawingFillLayerId,
        type: 'fill',
        source: this.tempDrawingSourceId,
        paint: { 'fill-color': '#3f5a7a', 'fill-opacity': 0.3 },
      })

      this.map.addLayer({
        id: this.tempDrawingStrokeLayerId,
        type: 'line',
        source: this.tempDrawingSourceId,
        paint: {
          'line-color': '#3f5a7a',
          'line-width': 2,
          'line-opacity': 0.8,
          'line-dasharray': [5, 5],
        },
      })

      this.tempDrawingLayer = {
        sourceId: this.tempDrawingSourceId,
        fillLayerId: this.tempDrawingFillLayerId,
        strokeLayerId: this.tempDrawingStrokeLayerId,
      }
    }
  }

  private updateRectangleDrag(currentPoint: maplibregl.LngLat): void {
    if (!this.rectangleStartPoint || !this.tempDrawingLayer) {
      return
    }

    const minLng = Math.min(this.rectangleStartPoint.lng, currentPoint.lng)
    const maxLng = Math.max(this.rectangleStartPoint.lng, currentPoint.lng)
    const minLat = Math.min(this.rectangleStartPoint.lat, currentPoint.lat)
    const maxLat = Math.max(this.rectangleStartPoint.lat, currentPoint.lat)

    const rectCoords = [
      [minLng, minLat],
      [maxLng, minLat],
      [maxLng, maxLat],
      [minLng, maxLat],
      [minLng, minLat],
    ]

    try {
      if (this.map) {
        const source = this.map.getSource(this.tempDrawingSourceId)
        if (isGeoJSONSource(source)) {
          source.setData({
            type: 'Feature',
            geometry: { type: 'Polygon', coordinates: [rectCoords] },
            properties: {},
          })
        }
      }
    } catch (error) {
      log.error('🟪 ❌ Error updating rectangle bounds:', error)
    }
  }

  private async finishRectangleDrag(endPoint: maplibregl.LngLat): Promise<void> {
    if (!this.rectangleStartPoint) {
      return
    }

    const latDiff = Math.abs(this.rectangleStartPoint.lat - endPoint.lat)
    const lngDiff = Math.abs(this.rectangleStartPoint.lng - endPoint.lng)

    if (latDiff >= 0.0001 && lngDiff >= 0.0001) {
      const bounds = {
        ne: {
          lat: Math.max(this.rectangleStartPoint.lat, endPoint.lat),
          lng: Math.max(this.rectangleStartPoint.lng, endPoint.lng),
        },
        sw: {
          lat: Math.min(this.rectangleStartPoint.lat, endPoint.lat),
          lng: Math.min(this.rectangleStartPoint.lng, endPoint.lng),
        },
      }
      await this.createRectangleFeature(bounds)
    } else {
      log.debug('🚫 Rectangle too small, canceling')
    }

    this.cleanupRectangleDrag()
    this.callbacks.enableMapPanning()
  }

  cleanupRectangleDrag(): void {
    this.rectangleStartPoint = undefined
    this.removeTempDrawing()
  }

  /**
   * Tear down the temp-drawing layers/source if present. The `temp-drawing`
   * id is shared with the line/polygon manager, and cairn-map runs every
   * manager's cleanup in sequence — so by the time this runs the layers may
   * already be gone. Guard each removal (and swallow teardown races) so we never
   * throw "no source with this ID" / "cannot remove non-existing layer".
   */
  private removeTempDrawing(): void {
    if (!this.tempDrawingLayer || !this.map) {
      this.tempDrawingLayer = undefined
      return
    }
    try {
      const { fillLayerId, strokeLayerId, sourceId } = this.tempDrawingLayer
      if (fillLayerId && this.map.getLayer(fillLayerId)) this.map.removeLayer(fillLayerId)
      if (strokeLayerId && this.map.getLayer(strokeLayerId)) this.map.removeLayer(strokeLayerId)
      if (this.map.getSource(sourceId)) this.map.removeSource(sourceId)
    } catch {
      // Map style may already be torn down, or the shared layers were removed
      // by another manager's cleanup — nothing left to do.
    }
    this.tempDrawingLayer = undefined
  }

  // === Selection Rectangle ===

  private startSelectionRect(latlng: maplibregl.LngLat, skipModeSwitch = false): void {
    // Starting a box-select drops any in-progress edit; selection is state-driven
    // now, so it no longer switches into a "select" mode.
    if (!skipModeSwitch) {
      this.callbacks.exitAllEditModes()
      this.callbacks.hideAllEditHandles()
      this.callbacks.updateContextMode()
    }

    this.selectionRectStartPoint = latlng

    const rectCoords = [
      [latlng.lng, latlng.lat],
      [latlng.lng, latlng.lat],
      [latlng.lng, latlng.lat],
      [latlng.lng, latlng.lat],
      [latlng.lng, latlng.lat],
    ]

    this.map!.addSource(this.selectionRectSourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [rectCoords] },
        properties: {},
      },
    })

    this.map!.addLayer({
      id: this.selectionRectFillLayerId,
      type: 'fill',
      source: this.selectionRectSourceId,
      paint: { 'fill-color': '#2563eb', 'fill-opacity': 0.1 },
    })

    this.map!.addLayer({
      id: this.selectionRectStrokeLayerId,
      type: 'line',
      source: this.selectionRectSourceId,
      paint: { 'line-color': '#2563eb', 'line-width': 2, 'line-dasharray': [6, 3] },
    })

    this.selectionRect = {
      sourceId: this.selectionRectSourceId,
      fillLayerId: this.selectionRectFillLayerId,
      strokeLayerId: this.selectionRectStrokeLayerId,
    }
  }

  private updateSelectionRect(latlng: maplibregl.LngLat): void {
    if (!this.selectionRect || !this.selectionRectStartPoint) {
      return
    }

    const minLng = Math.min(this.selectionRectStartPoint.lng, latlng.lng)
    const maxLng = Math.max(this.selectionRectStartPoint.lng, latlng.lng)
    const minLat = Math.min(this.selectionRectStartPoint.lat, latlng.lat)
    const maxLat = Math.max(this.selectionRectStartPoint.lat, latlng.lat)

    const rectCoords = [
      [minLng, minLat],
      [maxLng, minLat],
      [maxLng, maxLat],
      [minLng, maxLat],
      [minLng, minLat],
    ]

    const source = this.map!.getSource(this.selectionRectSourceId)
    if (isGeoJSONSource(source)) {
      source.setData({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [rectCoords] },
        properties: {},
      })
    }
  }

  private async finishSelectionRect(latlng: maplibregl.LngLat, additive = false): Promise<void> {
    if (!this.selectionRectStartPoint) {
      return
    }

    const minLng = Math.min(this.selectionRectStartPoint.lng, latlng.lng)
    const maxLng = Math.max(this.selectionRectStartPoint.lng, latlng.lng)
    const minLat = Math.min(this.selectionRectStartPoint.lat, latlng.lat)
    const maxLat = Math.max(this.selectionRectStartPoint.lat, latlng.lat)

    const bounds = new maplibregl.LngLatBounds([minLng, minLat], [maxLng, maxLat])
    this.selectionRectStartPoint = undefined

    if (this.selectionRect) {
      this.map!.removeLayer(this.selectionRect.fillLayerId)
      this.map!.removeLayer(this.selectionRect.strokeLayerId)
      this.map!.removeSource(this.selectionRect.sourceId)
      this.selectionRect = undefined
    }

    if (!additive) {
      selectionService.clearAll()
    }

    const selectionPoly = bboxPolygon([minLng, minLat, maxLng, maxLat])

    this.callbacks.getShapeComponents().forEach((component, featureId) => {
      const feature = component.feature

      let featureGeom: GeoJSON.Feature | true | null = null
      try {
        if (feature.type === 'point') {
          featureGeom = point((feature.geometry as GeoJSON.Point).coordinates)
        } else if (feature.type === 'line') {
          featureGeom = lineString((feature.geometry as GeoJSON.LineString).coordinates)
        } else if (feature.type === 'polygon' || feature.type === 'rectangle') {
          featureGeom = polygon((feature.geometry as GeoJSON.Polygon).coordinates)
        } else {
          const radius =
            feature.style?.radius ?? (feature.properties?.radius as number | undefined) ?? 100
          featureGeom = circle((feature.geometry as GeoJSON.Point).coordinates, radius / 1000, {
            units: 'kilometers',
          })
        }
      } catch {
        const shapeBounds = component.getLayerBounds()
        if (shapeBounds && bounds.intersects(shapeBounds)) {
          featureGeom = true
        }
      }

      if (!featureGeom) {
        return
      }

      let intersects = false
      if (featureGeom === true) {
        intersects = true
      } else {
        try {
          if (feature.type === 'point') {
            intersects = booleanPointInPolygon(featureGeom, selectionPoly)
          } else {
            intersects = !booleanDisjoint(featureGeom, selectionPoly)
          }
        } catch {
          const shapeBounds = component.getLayerBounds()
          intersects = !!(shapeBounds && bounds.intersects(shapeBounds))
        }
      }

      if (intersects) {
        if (!tripFeatureManager.getSelectedFeatures().includes(featureId)) {
          tripFeatureManager.toggleFeatureSelection(featureId)
        }
        component.setSelected(true)
      }
    })

    const overlayLayers = getSelectableOverlayLayerIds()
    if (overlayLayers.length > 0 && this.map) {
      const sw = this.map.project([minLng, minLat])
      const ne = this.map.project([maxLng, maxLat])
      const overlayHits = this.map.queryRenderedFeatures([sw, ne], { layers: overlayLayers })
      const seen = new Set<string>()
      const refs = []
      const trailBox = { minLng, minLat, maxLng, maxLat }
      for (const hit of overlayHits) {
        const ref = mapGeoJSONFeatureToRef(hit)
        if (!ref) continue
        if (ref.source === 'backcountry-trail') {
          // A merged trail bundles disconnected segments; keep only the
          // constituent lines actually inside the box (named or unnamed).
          for (const geometry of backcountryOverlayService.trailSegmentsInBox(
            ref.geometry,
            trailBox
          )) {
            const tref = { ...ref, geometry }
            const key = featureRefKey(tref)
            if (!seen.has(key)) {
              seen.add(key)
              refs.push(tref)
            }
          }
        } else {
          const key = featureRefKey(ref)
          if (!seen.has(key)) {
            seen.add(key)
            refs.push(ref)
          }
        }
      }
      // One notification for the whole box, not one re-highlight per feature.
      selectionService.selectMany(refs)
    }

    this.callbacks.enableMapPanning()
    await this.callbacks.updateSelectedFeatures()
    this.callbacks.updateContextMode()
  }

  // === Feature Creation ===

  private async createCircleFeature(center: maplibregl.LngLat, radius: number): Promise<void> {
    try {
      const feature = await tripFeatureManager.createCircleFeature(
        { lat: center.lat, lng: center.lng },
        radius
      )
      this.callbacks.onFeatureCreated(feature)

      tripFeatureManager.selectFeature(feature.id)
      this.callbacks.updateContextMode()

      const shapeComponent = this.callbacks.getShapeComponent(feature.id)
      if (shapeComponent) {
        shapeComponent.enterEditMode()
      }
    } catch (error) {
      log.error('Failed to create circle feature:', error)
    }
  }

  private async createRectangleFeature(bounds: {
    ne: FeatureLatLng
    sw: FeatureLatLng
  }): Promise<void> {
    try {
      const feature = await tripFeatureManager.createRectangleFeature(bounds)
      this.callbacks.onFeatureCreated(feature)

      tripFeatureManager.selectFeature(feature.id)
      this.callbacks.updateContextMode()

      const shapeComponent = this.callbacks.getShapeComponent(feature.id)
      if (shapeComponent) {
        shapeComponent.enterEditMode()
      }
    } catch (error) {
      log.error('Failed to create rectangle feature:', error)
    }
  }

  dispose(): void {
    if (this.map && this.boundNativeMouseDown) {
      this.map.getContainer().removeEventListener('mousedown', this.boundNativeMouseDown, true)
    }
    if (this.boundNativeDragMove) {
      document.removeEventListener('mousemove', this.boundNativeDragMove)
    }
    if (this.boundNativeDragEnd) {
      document.removeEventListener('mouseup', this.boundNativeDragEnd)
    }

    this.cleanupCircleDrag()
    this.cleanupRectangleDrag()

    if (this.selectionRect && this.map) {
      try {
        this.map.removeLayer(this.selectionRect.fillLayerId)
        this.map.removeLayer(this.selectionRect.strokeLayerId)
        this.map.removeSource(this.selectionRect.sourceId)
      } catch {
        // Layers/source may already be removed
      }
      this.selectionRect = undefined
    }
  }
}
